import {
  Box,
  VStack,
  HStack,
  Text,
  Switch,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { useNudgePreferencesQuery, useUpdateNudgePreference } from "../hooks";
import { NudgePreference } from "../types";

export default function NudgePreferences() {
  const { data: preferences, isLoading, error } = useNudgePreferencesQuery();
  const updatePreference = useUpdateNudgePreference();

  const handleToggle = async (preference: NudgePreference) => {
    await updatePreference.mutateAsync({
      id: preference.id,
      data: { enabled: !preference.enabled },
    });
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        Error loading preferences
      </Alert>
    );
  }

  const groupedPreferences = preferences?.reduce((acc, pref) => {
    // Use camelCase for frontend properties (backend snake_case is auto-converted)
    const type = pref.nudgeTemplate?.nudgeType;
    if (!type) {
      console.warn("NudgePreference missing nudgeTemplate:", pref);
      return acc;
    }
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(pref);
    return acc;
  }, {} as Record<string, NudgePreference[]>);

  const typeLabels: Record<string, string> = {
    "weekly_milestone_digest": "Weekly Digests",
    "milestone_reminder": "Milestone Reminders",
    "day_of_prompt": "Day-of Prompts",
    "task_overdue": "Task Overdue Alerts",
    "journey_stuck": "Journey Stuck Notifications",
  };

  return (
    <VStack spacing={6} align="stretch">
      <Heading size="md">Nudge Preferences</Heading>

      {preferences?.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          No nudge preferences available yet. Templates will appear here once configured.
        </Alert>
      )}

      {Object.entries(groupedPreferences || {}).map(([type, prefs]) => (
        <Box key={type}>
          <Heading size="sm" mb={3}>
            {typeLabels[type] || type}
          </Heading>
          <VStack spacing={3} align="stretch" pl={4}>
            {prefs.map((preference) => (
              <PreferenceRow
                key={preference.id}
                preference={preference}
                onToggle={() => handleToggle(preference)}
                isUpdating={updatePreference.isPending}
              />
            ))}
          </VStack>
          <Divider mt={4} />
        </Box>
      ))}
    </VStack>
  );
}

interface PreferenceRowProps {
  preference: NudgePreference;
  onToggle: () => void;
  isUpdating: boolean;
}

function PreferenceRow({ preference, onToggle, isUpdating }: PreferenceRowProps) {
  return (
    <HStack justify="space-between" p={3} borderWidth={1} borderRadius="md" bg="white">
      <VStack align="start" spacing={1} flex={1}>
        <Text fontWeight="medium">{preference.nudgeTemplate?.name}</Text>
        {preference.nudgeTemplate?.description && (
          <Text fontSize="sm" color="gray.600">
            {preference.nudgeTemplate.description}
          </Text>
        )}
        {preference.nudgeTemplate?.systemNudge && (
          <Badge colorScheme="purple" size="sm">System</Badge>
        )}
      </VStack>
      <Switch
        isChecked={preference.enabled}
        onChange={onToggle}
        isDisabled={isUpdating}
        size="lg"
      />
    </HStack>
  );
}
